import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { BottomNavigation } from "@/components/layout/BottomNavigation";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChevronLeft,
  ArrowLeft,
  Zap,
  Check,
  Battery,
  BatteryLow,
  BatteryMedium,
  BatteryFull,
  BatteryCharging
} from "lucide-react";

const ENERGY_LEVELS = [
  { id: 1, label: "Very Low", description: "Exhausted, need rest", icon: BatteryLow, color: "from-red-500/30 to-orange-500/30", textColor: "text-red-400" },
  { id: 2, label: "Low", description: "Tired, low motivation", icon: Battery, color: "from-orange-500/30 to-amber-500/30", textColor: "text-orange-400" },
  { id: 3, label: "Moderate", description: "Okay, functional", icon: BatteryMedium, color: "from-yellow-500/30 to-lime-500/30", textColor: "text-yellow-400" },
  { id: 4, label: "Good", description: "Energized, focused", icon: BatteryFull, color: "from-green-500/30 to-emerald-500/30", textColor: "text-green-400" },
  { id: 5, label: "Excellent", description: "Peak energy!", icon: BatteryCharging, color: "from-emerald-500/30 to-teal-500/30", textColor: "text-emerald-400" },
];

export default function EnergyCheckInPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedEnergy, setSelectedEnergy] = useState(null);
  const [notes, setNotes] = useState("");
  const [step, setStep] = useState("select"); // select, notes, complete
  const [saving, setSaving] = useState(false);

  const handleEnergySelect = (energy) => {
    setSelectedEnergy(energy);
  };

  const handleContinue = () => {
    if (selectedEnergy) {
      setStep("notes");
    }
  };

  const handleSave = async () => {
    if (!selectedEnergy) return;
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/journal`,
        {
          title: `Energy: ${selectedEnergy.label}`,
          body: notes || `Energy level: ${selectedEnergy.label} - ${selectedEnergy.description}`,
          tags: ["energy", `energy-${selectedEnergy.id}`],
          source: "energy_check"
        },
        { 
          withCredentials: true 
        }
      );
      setStep("complete");
      toast.success("Energy check-in saved!");
    } catch (error) {
      console.error("Failed to save energy:", error);
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = () => {
    navigate("/compass");
  };

  return (
    <div className="min-h-screen bg-[#0b2821] pb-24" data-testid="energy-check-in-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[#0b2821]/95 backdrop-blur-sm border-b border-[#a69373]/20">
        <div className="px-4 py-4 flex items-center gap-3">
          <button 
            onClick={() => navigate("/compass")}
            className="p-2 -ml-2"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center">
              <Zap className="w-4 h-4 text-[#a69373]" />
            </div>
            <h1 className="text-lg font-heading text-[#a69373]">Energy Check-In</h1>
          </div>
        </div>
      </header>

      <main className="px-4 py-6">
        {step === "select" && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-heading text-white mb-2">What's your energy level?</h2>
              <p className="text-white/60">Rate how energized you feel right now</p>
            </div>

            <div className="space-y-3">
              {ENERGY_LEVELS.map((energy) => {
                const Icon = energy.icon;
                return (
                  <button
                    key={energy.id}
                    onClick={() => handleEnergySelect(energy)}
                    className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                      selectedEnergy?.id === energy.id
                        ? "border-[#a69373] bg-[#a69373]/10"
                        : "border-white/10 hover:border-white/20"
                    }`}
                    data-testid={`energy-${energy.id}`}
                  >
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${energy.color} flex items-center justify-center`}>
                      <Icon className={`w-7 h-7 ${energy.textColor}`} />
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-white font-semibold">{energy.label}</p>
                      <p className="text-white/50 text-sm">{energy.description}</p>
                    </div>
                    {selectedEnergy?.id === energy.id && (
                      <div className="w-6 h-6 rounded-full bg-[#a69373] flex items-center justify-center">
                        <Check className="w-4 h-4 text-[#0b2821]" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <Button
              onClick={handleContinue}
              disabled={!selectedEnergy}
              className="w-full mt-6 bg-[#a69373] hover:bg-[#8a7a5f] text-[#0b2821] font-semibold"
              data-testid="continue-button"
            >
              Continue
            </Button>
          </div>
        )}

        {step === "notes" && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${selectedEnergy.color} flex items-center justify-center mb-4`}>
                {(() => {
                  const Icon = selectedEnergy.icon;
                  return <Icon className={`w-10 h-10 ${selectedEnergy.textColor}`} />;
                })()}
              </div>
              <h2 className="text-xl font-heading text-white">{selectedEnergy.label} Energy</h2>
              <p className="text-white/50 text-sm">{selectedEnergy.description}</p>
            </div>

            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <label className="text-white/60 text-sm mb-2 block">
                  What's affecting your energy? (optional)
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Sleep quality, stress, exercise, nutrition..."
                  className="min-h-[120px] bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  data-testid="energy-notes"
                />
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep("select")}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Back
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-[#a69373] hover:bg-[#8a7a5f] text-[#0b2821] font-semibold"
                data-testid="save-energy-button"
              >
                {saving ? "Saving..." : "Save Check-In"}
              </Button>
            </div>
          </div>
        )}

        {step === "complete" && (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-6">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-heading text-white mb-2">Energy Tracked!</h2>
            <p className="text-white/60 mb-8">
              Tracking your energy helps identify what boosts or drains you.
            </p>
            <Button
              onClick={handleFinish}
              className="bg-[#a69373] hover:bg-[#8a7a5f] text-[#0b2821] font-semibold"
              data-testid="finish-button"
            >
              Return to Home
            </Button>
          </div>
        )}
      </main>

      <BottomNavigation />
    </div>
  );
}
